import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import HomeIcon from "@redotech/redo-web/icon-old/home.svg";
import { groupBy } from "@redotech/util/map";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface ReentryChoiceState {
  next: StepId | undefined;
  name: string;
}

export interface ReentryState {
  choices: ReentryChoiceState[];
  message: string;
  attempted?: boolean;
  customTitle?: string;
}

export const REENTRY = (
  Details: React.ComponentType<StepTypeDetailsProps<ReentryState>>,
): StepType<ReentryState, ModelStep.Reentry> => ({
  Details: (props: StepTypeDetailsProps<ReentryState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.REENTRY],
  get information() {
    return t(
      "Special multiple choice question that can be used to re-enter the flow from other multiple choice steps",
    );
  },
  valid(state) {
    return (
      !!state.choices.length &&
      state.choices.every((choice) => choice.name && choice.next)
    );
  },
  Icon: HomeIcon,
  description(state) {
    return t("Base question");
  },
  downstream(state) {
    const choices = groupBy(
      state.choices.filter((choice) => choice.next !== undefined),
      (choice) => choice.next,
    );
    const result: StepDownstream[] = [];
    for (const [next, c] of choices.entries()) {
      result.push({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        id: next!,
        label: c.length === 1 ? c[0]?.name : `${c.length} choices`,
      });
    }
    return result;
  },
  maxDownstream(state) {
    return (
      groupBy(
        state.choices.filter((choice) => choice.next !== undefined),
        (choice) => choice.next,
      ).size +
      state.choices.filter((choice) => choice.next === undefined).length
    );
  },
  empty: { choices: [], message: "" },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      for (const choice of state.choices) {
        if (choice.next === stepId) {
          choice.next = undefined;
        }
      }
    });
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.REENTRY,
      message: "",
      options: state.choices.map((choice) => ({
        name: choice.name,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        next: id(choice.next!),
      })),
    };
  },
  fromModel(model, id) {
    return {
      customTitle: model.customTitle,
      choices: model.options.map((option) => ({
        name: option.name,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        next: id(option.next!),
      })),
      message: "",
    };
  },
});
